import * as React from "react";
import { useState, useEffect, useMemo, useCallback } from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { apiEndpoint } from "../config/api";
import { apiFetch } from "../services/api";
import { rollupCandidates, summarizeCandidates, STATUS_META } from "../utils/onboardingRollup";

// Onboarding Candidates — READ-ONLY discovery surface (design
// docs/projects/onboarding-candidates-view-design.md). One row per LK/SI-flagged Dash company, with a
// readiness summary, so a steward can SEE which franchises still need warehouse onboarding (dbt YAML +
// Fivetran, done manually) before the LK/SI team reports missing data. Advisory, not approval; no writes.
// Standalone Administration tab (D1). Status is derived client-side by onboardingRollup (D2); the list
// shows ALL rows action-first with fully-onboarded rows subdued and last (D3).

// Chip presentation per status (labels come from STATUS_META; colour/variant are presentation-only).
const STATUS_CHIP = {
  needs_onboarding: { color: "warning", variant: "filled" },
  data_landed_unconfigured: { color: "default", variant: "outlined" },
  onboarded: { color: "success", variant: "outlined" },
};

// Display truthiness for the raw 0/1 readiness facts (see onboardingRollup for the same guard).
const yes = (v) => v === 1 || v === true || v === "1";

// Column headers + on-page definitions (shown as header tooltips). Order MUST match the body cells
// rendered in tableRows. Definitions are worded to keep the three axes distinct — the isLKSI flag (entry
// criterion), warehouse onboarding (data landed), and the LK/SI eligibility period — since those are easy
// to conflate.
const COLUMNS = [
  { label: "Com Code", align: "left", tip: "The franchise's normalized Dash company code (com_code)." },
  { label: "Franchise Name", align: "left", tip: "Company name from Dash companymeta." },
  { label: "Status", align: "left", tip: "Derived state (not stored): Not onboarded (no warehouse data) → No eligibility period (data present, no LK/SI eligibility period) → Onboarded. Advisory — every row is here only because the self-reported isLKSI flag is set." },
  { label: "Warehouse Data", align: "center", tip: "Whether operational data has landed in the warehouse — any dim_season / dim_facility rows for this franchise, i.e. we hold historical seasons/facilities. No = never extracted (Fivetran) / not in the dbt config." },
  { label: "Class Seasons", align: "right", tip: "Count of class-type seasons in the warehouse for this franchise (dim_season, season_type = 'c')." },
  { label: "Eligibility", align: "center", tip: "Whether this franchise has an LK/SI eligibility period configured in Season Manager's 'LK/SI Eligibility Administration' tab (app.lksi_franchise_enablement_period). 'Configured' = a period exists; '—' = none, so it fails open (governed by the raw LK/SI flag). This is the Season Manager config you set when a new franchise is onboarded — separate from the Dash isLKSI flag and from warehouse data." },
  { label: "First Seen", align: "left", tip: "createDate from Dash companymeta — when the company was first created in Dash." },
  { label: "Last Synced", align: "left", tip: "Last time this company's Dash companymeta record changed (Fivetran _fivetran_synced) — a metadata-freshness proxy, NOT season/operational activity." },
  { label: "Com ID", align: "right", tip: "Dash company id (comid); disambiguates duplicate com_codes." },
];

export default function OnboardingCandidates() {
  const [candidates, setCandidates] = useState([]);
  const [error, setError] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [query, setQuery] = useState("");

  const load = useCallback(() => {
    return apiFetch(`${apiEndpoint}/franchise-onboarding-candidates`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        setCandidates(data.candidates || []);
        setError(false);
        setLoaded(true);
      })
      .catch((e) => {
        console.log("franchise-onboarding-candidates failed:", e);
        setError(true);
        setLoaded(true);
      });
  }, []);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => rollupCandidates(candidates), [candidates]);
  const summary = useMemo(() => summarizeCandidates(rows), [rows]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        String(r.com_code).toLowerCase().includes(q) ||
        String(r.franchise_name || "").toLowerCase().includes(q)
    );
  }, [rows, query]);

  const summaryChips = [
    { label: "Candidates", value: summary.total },
    { label: "Not onboarded", value: summary.needsOnboarding },
    { label: "No eligibility period", value: summary.dataLandedUnconfigured },
    { label: "Onboarded", value: summary.onboarded },
  ].map((s) => (
    <Chip key={s.label} size="small" variant="outlined" label={`${s.label}: ${s.value}`} />
  ));

  const tableRows = filtered.map((r) => {
    const chip = STATUS_CHIP[r.status];
    const subdued = r.status === "onboarded";
    return (
      <TableRow key={r.com_code} sx={subdued ? { opacity: 0.55 } : undefined}>
        <TableCell>{r.com_code}</TableCell>
        <TableCell>{r.franchise_name}</TableCell>
        <TableCell>
          <Chip size="small" color={chip.color} variant={chip.variant} label={STATUS_META[r.status].label} />
        </TableCell>
        <TableCell align="center">{yes(r.warehouse_data_found) ? "Yes" : "No"}</TableCell>
        <TableCell align="right">{r.season_count ?? 0}</TableCell>
        <TableCell align="center">{yes(r.has_eligibility) ? "Configured" : "—"}</TableCell>
        <TableCell>{r.created_date || "—"}</TableCell>
        <TableCell>{r.last_synced || "—"}</TableCell>
        <TableCell align="right">{r.com_id ?? "—"}</TableCell>
      </TableRow>
    );
  });

  const headerCells = COLUMNS.map((c) => (
    <TableCell key={c.label} align={c.align}>
      <Tooltip title={c.tip} arrow placement="top">
        <span style={{ cursor: "help", textDecoration: "underline dotted", textUnderlineOffset: 3 }}>
          {c.label}
        </span>
      </Tooltip>
    </TableCell>
  ));

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Franchises that Season Manager can see as LK/SI candidates. Use this to spot a franchise that needs
        warehouse onboarding <strong>before</strong> the LK/SI team reports its data missing. A{" "}
        <strong>Needs onboarding</strong> row is flagged LK/SI but has no operational data in the warehouse
        yet — the cue to configure the dbt sources and Fivetran connector. This view is advisory and
        read-only; it configures nothing. Shows Dash companies flagged LK/SI — a company must have the LK/SI
        flag set in Dash to appear here.
      </Typography>

      {error ? (
        <Typography variant="body2" color="error">
          Unable to load onboarding candidates. Please try again later.
        </Typography>
      ) : !loaded ? (
        <Typography variant="body2" color="text.secondary">Loading…</Typography>
      ) : rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No onboarding candidates to display.</Typography>
      ) : (
        <>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1.5, alignItems: "center" }}>
            {summaryChips}
            <Box sx={{ flexGrow: 1 }} />
            <TextField
              size="small"
              placeholder="Filter by com code or name"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              sx={{ minWidth: 240 }}
            />
          </Box>
          <Paper variant="outlined" sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: 1000 }}>
              <TableHead>
                <TableRow>{headerCells}</TableRow>
              </TableHead>
              <TableBody>{tableRows}</TableBody>
            </Table>
          </Paper>
        </>
      )}
    </Box>
  );
}
